/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Language normalization helpers. Maps region-specific or browser-mutated codes
 * (e.g. Chrome Translate turning `pt-BR` into `pt`) back to a supported locale.
 */

/**
 * Supported languages in the application
 */
export const SUPPORTED_LANGUAGES = ['en', 'pt-BR', 'es'] as const;

/**
 * The locale every visitor gets until they ask for another one. Housien sells
 * only in Brazil, so the browser's own language is deliberately *not* a signal:
 * a crawler rendering as en-US used to flip the public pages to English and put
 * an English title on a .com.br product. Language now changes only on an
 * explicit choice (stored in localStorage) or a `?lang=` URL.
 */
export const DEFAULT_LANGUAGE = 'pt-BR';

/**
 * Maps language variants to their canonical supported locale.
 * Chrome Translate mutates `<html lang>` from region-specific (pt-BR) to generic (pt),
 * causing i18n to lose the correct resource bundle.
 */
const LANGUAGE_NORMALIZATION_MAP: Record<string, string> = {
  'pt': 'pt-BR',
  'pt-PT': 'pt-BR',
  'pt-br': 'pt-BR',
  'en-US': 'en',
  'en-GB': 'en',
  'en-AU': 'en',
  'es-AR': 'es',
  'es-MX': 'es',
  'es-CL': 'es',
  'es-CO': 'es',
};

/**
 * Maps base language to supported language.
 *
 * @param {string} baseLang - Base language code
 * @returns {string | null} Supported language or null
 */
export function mapBaseLanguageToSupported(baseLang: string): string | null {
  if (baseLang === 'pt') {
    return 'pt-BR';
  }
  if (baseLang === 'en') {
    return 'en';
  }
  if (baseLang === 'es') {
    return 'es';
  }
  return null;
}

/**
 * Normalizes a language code to one of the supported locales.
 * Handles the Chrome Translate scenario where `pt-BR` becomes `pt`.
 *
 * @param {string} lang - Raw language code (e.g. "pt", "pt-BR", "en-US")
 * @returns {string} Canonical supported language code
 */
export function normalizeLanguageCode(lang: string): string {
  if (SUPPORTED_LANGUAGES.includes(lang as any)) {
    return lang;
  }

  const normalized = LANGUAGE_NORMALIZATION_MAP[lang];
  if (normalized) {
    return normalized;
  }

  const baseLang = lang.split('-')[0].toLowerCase();
  const baseNormalized = LANGUAGE_NORMALIZATION_MAP[baseLang];
  if (baseNormalized) {
    return baseNormalized;
  }

  const mapped = mapBaseLanguageToSupported(baseLang);
  return mapped || DEFAULT_LANGUAGE;
}
